import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type CardAutoSaveOptions = {
  cardId?: string;
  draft: unknown;
  dirty: boolean;
  enabled: boolean;
  onSave: () => Promise<boolean>;
  delayMs?: number;
};

type SaveRequest = Pick<CardAutoSaveOptions, 'cardId' | 'draft' | 'onSave'>;

/** A session owns scheduled work; closing it must never resurrect a discarded draft. */
export function useCardAutoSave({ delayMs = 800, ...options }: CardAutoSaveOptions) {
  const latest = useRef(options);
  latest.current = options;
  const scheduled = useRef<SaveRequest | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const flushing = useRef<Promise<boolean> | null>(null);
  const attempts = useRef(new Map<string | undefined, { draft: unknown; saved: boolean }>());
  const previousCardId = useRef(options.cardId);
  const mounted = useRef(false);
  const [settled, setSettled] = useState(0);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    scheduled.current = null;
  }, []);

  const run = useCallback((request: SaveRequest) => {
    if (inFlight.current) return inFlight.current;
    const promise = Promise.resolve()
      .then(() => request.onSave())
      .catch(() => false)
      .then((saved) => {
        attempts.current.set(request.cardId, { draft: request.draft, saved });
        return saved;
      })
      .finally(() => {
        if (inFlight.current === promise) inFlight.current = null;
        // Another card/draft may have been selected while this save was running.
        // Let its debounce start after settlement instead of dropping its work.
        if (mounted.current) setSettled((count) => count + 1);
      });
    inFlight.current = promise;
    return promise;
  }, []);

  const flush = useCallback(() => {
    cancel();
    if (flushing.current) return flushing.current;
    const promise = (async () => {
      while (mounted.current) {
        const active = inFlight.current;
        if (active && !(await active)) return false;
        if (!mounted.current) return false;
        const current = latest.current;
        if (!current.dirty) return true;
        const attempted = attempts.current.get(current.cardId);
        if (attempted?.saved && Object.is(attempted.draft, current.draft)) return true;
        // A newer edit can arrive while the previous save is running. Done
        // waits for that latest draft too, rather than exiting after the old one.
        cancel();
        // enabled governs background saves; the explicit caller owns session
        // checks and may retry a failed draft. Any failed save stops this flush.
        if (!(await run(current))) return false;
      }
      return false;
    })().finally(() => {
      if (flushing.current === promise) flushing.current = null;
    });
    flushing.current = promise;
    return promise;
  }, [cancel, run]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancel();
    };
  }, [cancel]);

  useEffect(() => {
    const current = latest.current;
    const oldScheduled = scheduled.current;
    const changedCard = previousCardId.current !== current.cardId;
    previousCardId.current = current.cardId;
    cancel();
    if (changedCard && oldScheduled) void run(oldScheduled);
    if (!current.enabled) return;
    if (!current.dirty || inFlight.current) return;
    const attempted = attempts.current.get(current.cardId);
    // Success also waits for a new draft, so cache/refetch lag cannot submit
    // the same change again. Failure waits for an edit or an explicit flush.
    if (attempted && Object.is(attempted.draft, current.draft)) return;
    const request: SaveRequest = {
      cardId: current.cardId,
      draft: current.draft,
      onSave: () => {
        const now = latest.current;
        return now.cardId === current.cardId && Object.is(now.draft, current.draft) ? now.onSave() : current.onSave();
      },
    };
    scheduled.current = request;
    timer.current = setTimeout(() => {
      timer.current = null;
      scheduled.current = null;
      void run(request);
    }, delayMs);
  }, [cancel, delayMs, options.cardId, options.dirty, options.draft, options.enabled, run, settled]);

  return useMemo(() => ({ cancel, flush }), [cancel, flush]);
}
